import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Decimal,
  decimalToString,
  parseDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  type DecimalExpansion,
  decimalExpansion,
  expansionToText,
} from "@/lib/tool-kit/arithmetic/expansion";
import {
  type DivisionError,
  type DivisionMode,
  divisionFraction,
  type LongDivision,
  longDivide,
  quotientText,
} from "@/lib/tool-kit/arithmetic/long-division";

/**
 * Everything the long-division widget shows, computed in one place: the digit-by-digit
 * division, the exact fraction and decimal expansion behind it, and the sentences that
 * narrate each step. The sentence builders take the widget's own strings so they stay
 * translatable.
 */
export interface DivisionView {
  division: LongDivision;
  expansion: DecimalExpansion;
  fraction: { num: bigint; den: bigint };
  dividendText: string;
  divisorText: string;
  answerText: string;
}

export type DivisionComputation =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: DivisionError }
  | { kind: "ok"; view: DivisionView };

/** The strings the narration needs. */
export interface DivisionWords {
  start: string;
  bringDown: string;
  appendZero: string;
  goesIn: string;
  times: string;
  zeroTimes: string;
  endExact: string;
  endRemainder: string;
  endRepeat: string;
  endPlaces: string;
  places: string;
}

export const MAX_STEPS_LISTED = 60;
export const MAX_REPEAT_SHOWN = 30;

/** A repeating block clipped to a readable length. */
export function shortExpansion(e: DecimalExpansion): string {
  const clipped =
    e.repeating.length > MAX_REPEAT_SHOWN
      ? { ...e, repeating: `${e.repeating.slice(0, MAX_REPEAT_SHOWN)}…` }
      : e;
  return expansionToText(clipped);
}

function answerFor(
  division: LongDivision,
  expansion: DecimalExpansion,
  remainderShort: string
): string {
  if (division.mode === "remainder") {
    const tail = division.exact
      ? ""
      : ` ${remainderShort} ${division.remainder.toString()}`;
    return `${quotientText(division)}${tail}`;
  }
  return shortExpansion(expansion);
}

function textOf(d: Decimal): string {
  return decimalToString(d);
}

export function computeDivision(
  rawA: string,
  rawB: string,
  mode: DivisionMode,
  places: number,
  remainderShort: string
): DivisionComputation {
  if (rawA.trim() === "" || rawB.trim() === "") {
    return { kind: "empty" };
  }
  const a = parseDecimal(rawA);
  const b = parseDecimal(rawB);
  if (a === null || b === null) {
    return { kind: "invalid" };
  }
  const division = longDivide(a, b, { mode, places });
  if ("error" in division) {
    return { kind: division.error };
  }
  const fraction = divisionFraction(a, b);
  const expansion = decimalExpansion(fraction.num, fraction.den);
  return {
    kind: "ok",
    view: {
      division,
      expansion,
      fraction,
      dividendText: textOf(a),
      divisorText: textOf(b),
      answerText: answerFor(division, expansion, remainderShort),
    },
  };
}

export function stepSentences(
  division: LongDivision,
  words: DivisionWords,
  locale: string
): { lines: string[]; capped: boolean } {
  const divisor = division.divisor.toString();
  const lines: string[] = [];
  const first = division.steps.findIndex((s) => s.quotientDigit > 0);
  const begin = first === -1 ? division.steps.length - 1 : first;
  const total = division.steps.length;
  for (let i = begin; i < total && lines.length < MAX_STEPS_LISTED; i++) {
    const step = division.steps[i];
    const current = step.current.toString();
    let intro = t(words.bringDown, { digit: step.digit });
    if (i === begin) {
      intro = t(words.start, { divisor, current });
    } else if (step.appended) {
      intro = t(words.appendZero, { current });
    }
    const body =
      step.quotientDigit === 0
        ? t(words.zeroTimes, { divisor, current })
        : t(words.goesIn, {
            divisor,
            current,
            times: tPlural(words.times, locale, step.quotientDigit).trim(),
            quotient: step.quotientDigit,
            product: step.product.toString(),
            remainder: step.remainder.toString(),
          });
    lines.push(`${intro} ${body}`);
  }
  return { lines, capped: begin + lines.length < total };
}

export function endingSentence(
  division: LongDivision,
  expansion: DecimalExpansion,
  words: DivisionWords,
  locale: string
): string {
  if (division.exact) {
    return words.endExact;
  }
  if (division.mode === "remainder") {
    return t(words.endRemainder, {
      remainder: division.remainder.toString(),
    });
  }
  if (division.repeatStart !== null) {
    return t(words.endRepeat, {
      remainder: division.remainder.toString(),
      block: division.quotientFraction.slice(division.repeatStart),
    });
  }
  return t(words.endPlaces, {
    places: tPlural(
      words.places,
      locale,
      division.quotientFraction.length
    ).trim(),
    exact: shortExpansion(expansion),
  });
}
